import React, { useEffect, useMemo } from 'react';
import { Alert, Card, Empty, Typography } from 'antd'; // Removed unused imports: Col, Form, Image, Row
import { MethodSkeleton } from './MethodSkeleton';
import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import {
  fetchPaymentMethods,
  selectPaymentLastParams,
  selectPaymentLoading,
  selectPaymentMethods,
  selectPaymentStatus,
  setShippingMethodId,
} from '@store/slices/paymentsSlice.tsx';
// Removed unused imports: GenericForm, FieldType, FieldValue, IMG_NOT_FOUND
import { useAutoSelectSingleOption } from '@eshop/hooks/useCheckoutFlow.ts';
import { PaymentMethodCard } from './PaymentMethodCard';
import './PaymentMethodCard.css';

interface PaymentMethodSectionProps {
  shippingMethod: number | undefined;
  useShipping: boolean;
  selectedPaymentMethodId?: number;
  onPaymentMethodChange: (methodId: number | undefined) => void;
  className?: string;
  onAutoSelectNext?: () => void;
}

export const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  shippingMethod,
  useShipping,
  selectedPaymentMethodId,
  onPaymentMethodChange,
  className,
  onAutoSelectNext,
}) => {
  const dispatch = useAppDispatch();
  const paymentMethods = useAppSelector(selectPaymentMethods);
  const loading = useAppSelector(selectPaymentLoading);
  const status = useAppSelector(selectPaymentStatus);
  const lastParams = useAppSelector(selectPaymentLastParams);
  const cart = useAppSelector((s) => s.cart);
  const cartReady = cart.hasLoaded || cart.cartItems.length > 0;
  const takeaway = !useShipping;
  const canLoadMethods = cartReady && (!useShipping || typeof shippingMethod === 'number');
  const currentParams = useMemo(() => ({
    smid: useShipping ? shippingMethod : undefined,
    takeaway,
  }), [shippingMethod, takeaway, useShipping]);
  const paramsMatch = JSON.stringify(lastParams ?? null) === JSON.stringify(currentParams);
  const showLoading = canLoadMethods && (loading || !paramsMatch);

  const { markManualSelection } = useAutoSelectSingleOption({
    options: paymentMethods,
    selectedValue: selectedPaymentMethodId,
    isLoading: showLoading,
    getOptionValue: (method) => method.id,
    onSelect: (id) => {
      onPaymentMethodChange(id);
    },
    onAutoSelect: () => {
      onAutoSelectNext?.();
    },
    disabled: !canLoadMethods,
  });

  useEffect(() => {
    dispatch(setShippingMethodId(shippingMethod));
  }, [dispatch, shippingMethod]);

  useEffect(() => {
    // Evitamos llamadas 404 asegurándonos de disparar la carga sólo cuando el carrito está listo.
    if (!canLoadMethods) {
      return;
    }
    dispatch(fetchPaymentMethods(currentParams));
  }, [dispatch, currentParams, canLoadMethods]);

  useEffect(() => {
    if (!selectedPaymentMethodId || showLoading) {
      return;
    }
    if (!paymentMethods.some((method) => method.id === selectedPaymentMethodId)) {
      onPaymentMethodChange(undefined);
    }
  }, [onPaymentMethodChange, paymentMethods, selectedPaymentMethodId, showLoading]);

  const handleMethodClick = (id: number) => {
    markManualSelection();
    onPaymentMethodChange(id);
  };

  return (
    <Card
      title="Método de Pago"
      className={`${className} bg-transparent`} // Transparency for cleaner look
      variant="borderless"
      styles={{
        header: { borderBottom: 'none', paddingLeft: 0, fontSize: '1.1rem' },
        body: { padding: 0 }
      }}
    >
      {canLoadMethods ? (
        showLoading ? (
          <MethodSkeleton count={4} />
        ) : paymentMethods.length > 0 ? (
          <>
            {paymentMethods.some((method) => method.is_fallback) ? (
              <Alert
                type="info"
                showIcon
                title="Pago a coordinar"
                description="No encontramos un medio de pago compatible. Podrás acordarlo con la tienda después de confirmar el pedido."
                className="mb-3"
              />
            ) : null}
            <div className="payment-methods-grid">
              {paymentMethods.map((method) => (
                <PaymentMethodCard
                  key={method.id}
                  id={method.id}
                  name={method.name}
                  imageUrl={method.image?.thumbnails?.default} // Accessing image URL from prop
                  isSelected={selectedPaymentMethodId === method.id}
                  onClick={() => handleMethodClick(method.id)}
                  disabled={loading}
                />
              ))}
            </div>
          </>
        ) : status === 'failed' ? (
          <Typography.Text type="danger">
            No pudimos cargar los métodos de pago. Intentá nuevamente.
          </Typography.Text>
        ) : (
          <Empty description="No hay métodos de pago disponibles" />
        )
      ) : (
        <Typography.Text type="secondary">
          Debes elegir un método de envío para ver las opciones de pago.
        </Typography.Text>
      )}
    </Card>
  );
};
